import { useEffect, useState } from "react";
import { monthlyReport, exportCsvUrl, exportXlsxUrl } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, FileBarChart2, FileSpreadsheet } from "lucide-react";
import { useBranch } from "@/context/BranchContext";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function Reports() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { branchId } = useBranch();

  const load = async () => {
    setLoading(true);
    try {
      setData(await monthlyReport(year, month));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, branchId]);

  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) yearOptions.push(y);

  const startDate = `${year}-${String(month).padStart(2, "0")}-01`;
  const endDay = new Date(year, month, 0).getDate();
  const endDate = `${year}-${String(month).padStart(2, "0")}-${String(endDay).padStart(2, "0")}`;
  const csvHref = exportCsvUrl({ start_date: startDate, end_date: endDate });
  const xlsxHref = exportXlsxUrl({ start_date: startDate, end_date: endDate });

  const totals = data?.totals || {};
  const rows = data?.per_delivery_boy || [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">Analytics</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-1">
            Monthly Report
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Per delivery boy totals for the selected month. Export a CSV for accounting.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs text-zinc-500">Month</label>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger className="w-40" data-testid="report-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {MONTHS.map((m, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-zinc-500">Year</label>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-28" data-testid="report-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <a href={xlsxHref} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-lg" data-testid="report-export-xlsx-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
            </Button>
          </a>
          <a href={csvHref} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-lg" data-testid="report-export-csv-btn">
              <Download className="h-4 w-4 mr-1.5" /> CSV
            </Button>
          </a>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard label="Entries" value={totals.entries || 0} isCurrency={false} />
        <StatCard label="Cash Collected" value={totals.cash_collected} />
        <StatCard label="Final Payable" value={totals.final_payable} />
        <StatCard label="Paid" value={totals.paid_amount} tone="positive" />
        <StatCard label="Balance" value={totals.balance} tone="warning" />
        <StatCard label="Min Guarantee" value={totals.min_guarantee_applied} tone="danger" />
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              <Th>Delivery Boy</Th>
              <Th className="text-right">Entries</Th>
              <Th className="text-right">Cash Collected</Th>
              <Th className="text-right">Earning</Th>
              <Th className="text-right">Shop Ded.</Th>
              <Th className="text-right">Other Ded.</Th>
              <Th className="text-right">Payable</Th>
              <Th className="text-right">Paid</Th>
              <Th className="text-right">Balance</Th>
              <Th className="text-right">MG Applied</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={10} className="text-center py-8 text-zinc-500">Loading…</td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center py-14">
                  <div className="inline-flex flex-col items-center gap-2 text-zinc-500">
                    <FileBarChart2 className="h-8 w-8 text-zinc-300" />
                    <div className="font-medium text-zinc-700">No data for {MONTHS[month - 1]} {year}</div>
                    <div className="text-xs">Record some collection entries to populate this report.</div>
                  </div>
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.delivery_boy_id} className="border-b border-zinc-100 hover:bg-zinc-50 tabular">
                <td className="px-4 py-3 font-medium text-zinc-900">{r.delivery_boy_name || "—"}</td>
                <td className="px-4 py-3 text-right">{r.entries}</td>
                <td className="px-4 py-3 text-right">{formatINR(r.cash_collected)}</td>
                <td className="px-4 py-3 text-right">{formatINR(r.earning)}</td>
                <td className="px-4 py-3 text-right">{formatINR(r.shop_deduction)}</td>
                <td className="px-4 py-3 text-right">{formatINR(r.other_deduction)}</td>
                <td className="px-4 py-3 text-right font-semibold">{formatINR(r.final_payable)}</td>
                <td className="px-4 py-3 text-right text-emerald-700">{formatINR(r.paid_amount)}</td>
                <td className={`px-4 py-3 text-right font-semibold ${r.balance > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                  {formatINR(r.balance)}
                </td>
                <td className="px-4 py-3 text-right text-rose-600">{formatINR(r.min_guarantee_applied)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 ${className}`}>
    {children}
  </th>
);

const StatCard = ({ label, value, tone = "default", isCurrency = true }) => {
  const cls = {
    default: "text-zinc-950",
    positive: "text-emerald-700",
    warning: "text-amber-700",
    danger: "text-rose-600",
  }[tone];
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-4">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-2 font-display text-xl md:text-2xl font-semibold tabular ${cls}`}>
        {isCurrency ? formatINR(value) : value}
      </div>
    </div>
  );
};
